import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { createAction } from 'redux-act';
import { url } from 'utils/url';

export const PROGRAMMATIC_VENDOR_FETCH_INIT = createAction(
  'PROGRAMMATIC_VENDOR_FETCH_INIT'
);
export const PROGRAMMATIC_VENDOR_FETCH_SUCCESS = createAction(
  'PROGRAMMATIC_VENDOR_FETCH_SUCCESS'
);
export const PROGRAMMATIC_VENDOR_FETCH_FAIL = createAction(
  'PROGRAMMATIC_VENDOR_FETCH_FAIL'
);

export const PROGRAMMATIC_VENDOR_CREATE_INIT = createAction(
  'PROGRAMMATIC_VENDOR_CREATE_INIT'
);
export const PROGRAMMATIC_VENDOR_CREATE_SUCCESS = createAction(
  'PROGRAMMATIC_VENDOR_CREATE_SUCCESS'
);
export const PROGRAMMATIC_VENDOR_CREATE_FAIL = createAction(
  'PROGRAMMATIC_VENDOR_CREATE_FAIL'
);

export const PROGRAMMATIC_VENDOR_UPDATE_INIT = createAction(
  'PROGRAMMATIC_VENDOR_UPDATE_INIT'
);
export const PROGRAMMATIC_VENDOR_UPDATE_SUCCESS = createAction(
  'PROGRAMMATIC_VENDOR_UPDATE_SUCCESS'
);
export const PROGRAMMATIC_VENDOR_UPDATE_FAIL = createAction(
  'PROGRAMMATIC_VENDOR_UPDATE_FAIL'
);

export const PROGRAMMATIC_VENDOR_DELETE_INIT = createAction(
  'PROGRAMMATIC_VENDOR_DELETE_INIT'
);
export const PROGRAMMATIC_VENDOR_DELETE_SUCCESS = createAction(
  'PROGRAMMATIC_VENDOR_DELETE_SUCCESS'
);
export const PROGRAMMATIC_VENDOR_DELETE_FAIL = createAction(
  'PROGRAMMATIC_VENDOR_DELETE_FAIL'
);

export const PROGRAMMATIC_VENDOR_CLEAR = createAction(
  'PROGRAMMATIC_VENDOR_CLEAR'
);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  'Content-Type': 'application/json',
});

const errMsg = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.details ||
  error?.message ||
  fallback;

// The API never returns apiKeyEncrypted. Strip it defensively so a backend
// regression can never leak a stored key into redux/devtools.
const sanitize = (vendor) => {
  if (!vendor) return vendor;
  const { apiKeyEncrypted, ...safe } = vendor;
  return safe;
};

// The list endpoint is scoped by templateId. Vendors created without a
// templateId are site-wide; the backend returns those too.
export const fetchProgrammaticVendors = ({ templateId, siteId } = {}) => {
  return async (dispatch) => {
    dispatch(PROGRAMMATIC_VENDOR_FETCH_INIT());
    try {
      const response = await axios.get(
        `${url}/programmaticVendor/${templateId || 'all'}`,
        { headers: authHeaders(), params: siteId ? { siteId } : undefined }
      );
      const list = (response.data.data || []).map(sanitize);
      return dispatch(PROGRAMMATIC_VENDOR_FETCH_SUCCESS({ list }));
    } catch (error) {
      const msg = errMsg(error, 'Failed to fetch programmatic vendors');
      toastr.error('Error Fetching Programmatic Vendors', msg);
      return dispatch(PROGRAMMATIC_VENDOR_FETCH_FAIL({ error: msg }));
    }
  };
};

export const getProgrammaticVendor = ({ id }, cb) => {
  return async (dispatch) => {
    dispatch(PROGRAMMATIC_VENDOR_FETCH_INIT());
    try {
      const response = await axios.post(
        `${url}/programmaticVendor/get`,
        { id },
        { headers: authHeaders() }
      );
      const vendor = sanitize(response.data.data);
      dispatch(PROGRAMMATIC_VENDOR_FETCH_SUCCESS({ vendor }));
      if (cb) cb(vendor);
      return vendor;
    } catch (error) {
      const msg = errMsg(error, 'Failed to fetch programmatic vendor');
      toastr.error('Error Fetching Programmatic Vendor', msg);
      dispatch(PROGRAMMATIC_VENDOR_FETCH_FAIL({ error: msg }));
      throw error;
    }
  };
};

export const createProgrammaticVendor = (payload, cb) => {
  return async (dispatch) => {
    dispatch(PROGRAMMATIC_VENDOR_CREATE_INIT());
    try {
      const response = await axios.post(
        `${url}/programmaticVendor/create`,
        payload,
        { headers: authHeaders() }
      );
      const created = sanitize(response.data.data);
      toastr.success('Success', 'Programmatic vendor created');
      dispatch(PROGRAMMATIC_VENDOR_CREATE_SUCCESS({ vendor: created }));
      if (cb) cb(created);
      return created;
    } catch (error) {
      const msg = errMsg(error, 'Failed to create programmatic vendor');
      toastr.error('Error Creating Programmatic Vendor', msg);
      dispatch(PROGRAMMATIC_VENDOR_CREATE_FAIL({ error: msg }));
      throw error;
    }
  };
};

export const updateProgrammaticVendor = (payload, cb) => {
  return async (dispatch) => {
    dispatch(PROGRAMMATIC_VENDOR_UPDATE_INIT());
    try {
      const response = await axios.post(
        `${url}/programmaticVendor/edit`,
        payload,
        { headers: authHeaders() }
      );
      const updated = sanitize(response.data.data);
      toastr.success('Success', 'Programmatic vendor updated');
      dispatch(PROGRAMMATIC_VENDOR_UPDATE_SUCCESS({ vendor: updated }));
      if (cb) cb(updated);
      return updated;
    } catch (error) {
      const msg = errMsg(error, 'Failed to update programmatic vendor');
      toastr.error('Error Updating Programmatic Vendor', msg);
      dispatch(PROGRAMMATIC_VENDOR_UPDATE_FAIL({ error: msg }));
      throw error;
    }
  };
};

export const deleteProgrammaticVendor = ({ id }, cb) => {
  return async (dispatch) => {
    dispatch(PROGRAMMATIC_VENDOR_DELETE_INIT());
    try {
      await axios.delete(`${url}/programmaticVendor/delete`, {
        headers: authHeaders(),
        params: { id },
      });
      toastr.success('Success', 'Programmatic vendor deleted');
      dispatch(PROGRAMMATIC_VENDOR_DELETE_SUCCESS({ id }));
      if (cb) cb();
    } catch (error) {
      const msg = errMsg(error, 'Failed to delete programmatic vendor');
      toastr.error('Error Deleting Programmatic Vendor', msg);
      dispatch(PROGRAMMATIC_VENDOR_DELETE_FAIL({ error: msg }));
      throw error;
    }
  };
};

export const clearProgrammaticVendors = () => (dispatch) =>
  dispatch(PROGRAMMATIC_VENDOR_CLEAR());
